import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Input from '../../components/inputs/text-field-input';
import { ResponseWithSuccess } from '../../core/client/Models';
import { CustomButton } from '../../components/buttons';
import { SentimentDissatisfied, TaskAlt } from '@mui/icons-material';
import { get, post } from '../../core/client';
import { IsNullOrEmpty } from '../../core/utility';
import JoinFormUndeageContextProvider, { JoinFormUndeageContext, LegalContractConsentType } from '../shared/JoinFormUndeageContext';
import reactStringReplace from 'react-string-replace';

import classes from './styles.module.scss';
import Alert from '@mui/material/Alert';

interface UnderagePermitInfo {
    relativeFullName: string;
    relativeBirthDate: string;
    relativeTcIdentityNo: string;
    orgMemberFullName: string;
    orgMemberTcIdentityNo: string;
    orgMemberAddress: string;
    orgMemberPhone: string;
    orgMemberEmail: string;
    isSpouse: boolean;
    isApproved: boolean;
}

interface ApproveUnderagePermitFormResponse extends ResponseWithSuccess {
    errorCode: string;
    errorMessage: string;
}

interface InputErrorModel {
    name: string;
    errorText: string;
}

enum FetchStatus {
    Loading,
    Success,
    Fail,
    Approved
}

interface RouteParams {
    guid?: string;
}

const initialPageData: UnderagePermitInfo = {
    relativeFullName: '',
    relativeBirthDate: '',
    relativeTcIdentityNo: '',
    orgMemberFullName: '',
    orgMemberTcIdentityNo: '',
    orgMemberAddress: '',
    orgMemberPhone: '',
    orgMemberEmail: '',
    isSpouse: false,
    isApproved: false
}

const UnderagePermitFormComponent = () => {
    const { legalTextLoading, legalTexts, loadLegalTexts, openLegalTextModal } = useContext(JoinFormUndeageContext);

    let routeParams: RouteParams = useParams();
    const guid = routeParams.guid ? routeParams.guid : null;

    //states
    const [pageModel, setPageModel] = useState<UnderagePermitInfo>(initialPageData);
    const [fetchStatus, setFetchStatus] = useState<number>(FetchStatus.Loading);
    const [approvedConsents, setApprovedConsents] = useState<Array<string>>([]);
    const [reCaptchaToken, setReCaptchaToken] = useState<string | null>('');
    const [loadingSendForm, setLoadingSendForm] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [inputErrors, setInputErrors] = useState<Array<InputErrorModel>>([]);
    const [isKvkkError, setIsKvkkError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const recaptchaRef = useRef<any>(null);

    useEffect(() => {
        if (!IsNullOrEmpty(guid)) {
            loadInfo(guid as string);
        }
        loadLegalTexts([
            LegalContractConsentType.VERI_IZNI,
            LegalContractConsentType.YKB_VERI_AKTARIM_IZNI,
            LegalContractConsentType.ETK_IZNI,
            LegalContractConsentType.YURT_DISI_VERI_IZNI,
            LegalContractConsentType.UYELIK_SOZLESMESI,
            LegalContractConsentType.YKB_AYDINLATMA
        ]);
    }, [guid])

    const isConsentApproved = (type: string) => {
        return approvedConsents.some(consent => consent === type);
    };

    const handleConsentCheckbox = (e: any) => {
        const consentType = e.target.name;
        let consents: Array<string> = [...approvedConsents];
        consents = consents.filter(x => x !== consentType);

        if (e.target.checked) {
            consents = [...consents, consentType];

            if (
                consentType === LegalContractConsentType.VERI_IZNI ||
                consentType === LegalContractConsentType.YURT_DISI_VERI_IZNI ||
                consentType === LegalContractConsentType.VAKIF_DERNEK_IZNI
            ) {
                openLegalTextModal(consentType as LegalContractConsentType);
            }
        }
        setApprovedConsents(consents);
    };

    const handleMembershipAggreement = () => {
        openLegalTextModal(LegalContractConsentType.UYELIK_SOZLESMESI);
    };

    const handleYkbAggreement = () => {
        openLegalTextModal(LegalContractConsentType.YKB_AYDINLATMA);
    };

    const isRequiredConsentsChecked = () => {
        const requiredConsents = legalTexts?.consents.filter(
            x => x.required && x.type !== LegalContractConsentType.UYELIK_SOZLESMESI && x.type !== LegalContractConsentType.YKB_AYDINLATMA,
        );
        if (requiredConsents) {
            for (let item of requiredConsents) {
                if (!isConsentApproved(item.type)) {
                    return false;
                }
            }
        }
        return true;
    };

    const onChangeCaptcha = async (token: string | null) => {
        setReCaptchaToken(token)
    }

    const handleChange = (e: any) => {
        setPageModel(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const loadInfo = async (guid: string) => {
        const { data, fail, errorMessage } = await get<UnderagePermitInfo>(`api/membership/get-underage-permit-form-info/${guid}`);
        if (data) {
            if (data.isApproved) {
                setFetchStatus(FetchStatus.Approved);
                return;
            }
            setPageModel(data);
            setFetchStatus(FetchStatus.Success);
        }
        if (fail) {
            setFetchStatus(FetchStatus.Fail);
        }
    }

    const setError = (name: keyof UnderagePermitInfo, errorText = 'Zorunlu alan') => {
        const error: InputErrorModel = { name, errorText };
        setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
    }

    const validate = (model: UnderagePermitInfo): boolean => {
        setIsKvkkError(false);
        setInputErrors([]);

        let isValid = true;

        if (IsNullOrEmpty(model.orgMemberAddress)) {
            setError('orgMemberAddress', 'Lütfen açık adres giriniz.');
            isValid = false;
        }
        if (!isRequiredConsentsChecked()) {
            setIsKvkkError(true);
            isValid = false;
        }

        return isValid;
    }

    const handleSend = async (model: UnderagePermitInfo) => {

        setErrorText('');

        if (!validate(model)) return;
        setLoadingSendForm(true);
        const request = {
            guid: guid,
            address: model.orgMemberAddress,
            reCaptchaToken: reCaptchaToken,
            isApprovedVeriIzni: isConsentApproved(LegalContractConsentType.VERI_IZNI),
            isApprovedYkbAktarim: isConsentApproved(LegalContractConsentType.YKB_VERI_AKTARIM_IZNI),
            commPermStatus: isConsentApproved(LegalContractConsentType.ETK_IZNI),
        }
        const { data, fail, errorMessage } = await post<ApproveUnderagePermitFormResponse>('api/membership/approve-underage-permit-form', request);
        setLoadingSendForm(false);
        if (data) {
            if (!data.success) {
                const errorMessage = data.errorMessage 
                    ? `${data.errorMessage} Detaylar için KoçAilem çağrı merkezi ile iletişime geçiniz.` 
                    : 'Form gönderilemedi. Lütfen daha sonra tekrar deneyiniz. Detaylar için KoçAilem çağrı merkezi ile iletişime geçebilirsiniz.';

                setErrorText(errorMessage);
            } else {
                setIsSuccess(true);
            }
        }

        if (fail) {
            setErrorText('Form gönderilemedi. Lütfen daha sonra tekrar deneyiniz.');
        }

        (window as any).grecaptcha.reset();
    }

    return (
        <div className={classes.underagePermitForm}>
            <div className={classes.overlay}>
                <div className={classes.container}>
                    {fetchStatus === FetchStatus.Loading ? (
                        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" paddingY={2}>
                            <p>Lütfen bekleyiniz...</p>
                            <CircularProgress />
                        </Box>
                    ) : fetchStatus === FetchStatus.Fail ? (
                        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" paddingY={2}>
                            <p>Beklenmeyen bir hata oluştu. Lütfen sistem yöneticisiyle iletişime geçiniz.</p>
                            <SentimentDissatisfied fontSize='large' />
                        </Box>
                    ) : fetchStatus === FetchStatus.Approved ? (
                        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" paddingY={2}>
                            <p>Form daha önce onaylanmıştır.</p>
                            <TaskAlt fontSize='large' />
                        </Box>
                    ) : (
                        <div className={classes.form}>
                            {
                                !isSuccess ? (
                                    <>
                                        <h1>EK ÜYELİK İZİN FORMU</h1>
                                        <header>
                                            <p><em>Ek Üyenin Bilgileri:</em></p>
                                            <Grid container spacing={2}>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='relativeFullName'
                                                        name='relativeFullName'
                                                        caption='18 Yaş Altı Üyenin Adı Soyadı'
                                                        readOnly
                                                        value={pageModel.relativeFullName}
                                                    />
                                                </Grid>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='relativeTcIdentityNo'
                                                        name='relativeTcIdentityNo'
                                                        caption='T.C. Kimlik Numarası'
                                                        readOnly
                                                        value={pageModel.relativeTcIdentityNo}
                                                    />
                                                </Grid>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='relativeBirthDate'
                                                        name='relativeBirthDate'
                                                        caption='Doğum Tarihi'
                                                        readOnly
                                                        value={pageModel.relativeBirthDate}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </header>
                                        <article>
                                            <p>Yukarıda bilgileri verilen kanuni temsilcisi olduğum 18 yaş altı çocuğun KoçAilem Programı’na üye olması, ödeme yapmak dahil olmak üzere KoçAilem Programı tarafından sunulan tüm özellikleri kullanması için hesap oluşturma ve diğer tüm işlemlere izin verdiğimi beyan ederim. Bu izin, tarafımca iptal edilene kadar geçerlidir.</p>
                                            <p>Çocuğun 18 yaşına girerek fiil ehliyetini kazanıncaya kadar KoçAilem Programı kapsamında gerçekleştireceği tüm işlemlerden tamamen sorumlu olduğumu ve tüm yasal, mali veya diğer yükümlülüğün tarafıma ait olacağını kabul ediyorum.</p>
                                            <p>Çocuğun, KoçAilem Programı aracılığıyla alışveriş yaparken güvenlik önlemlerine ve kullanım kurallarına uyacağını taahhüt ederim.</p>
                                            <p>Kanuni temsilcilik durumunun değişmesi durumunda ilgili değişikliği KoçAilem’e bildirim yükümlülüğünün kanuni temsilci olarak tarafıma ait olduğunu beyan ederim.</p>
                                        </article>
                                        <footer>
                                            <p><em>Kanuni Temsilcinin Bilgileri:</em></p>
                                            <Grid container spacing={2} marginBottom={2}>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='orgMemberFullName'
                                                        name='orgMemberFullName'
                                                        caption='Adı Soyadı'
                                                        readOnly
                                                        value={pageModel.orgMemberFullName}
                                                    />
                                                </Grid>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='orgMemberTcIdentityNo'
                                                        name='orgMemberTcIdentityNo'
                                                        caption='T.C. Kimlik Numarası'
                                                        readOnly
                                                        value={pageModel.orgMemberTcIdentityNo}
                                                    />
                                                </Grid>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='orgMemberPhone'
                                                        name='orgMemberPhone'
                                                        caption='Cep Telefonu'
                                                        readOnly
                                                        value={pageModel.orgMemberPhone}
                                                    />
                                                </Grid>
                                                <Grid item sm={6} xs={12}>
                                                    <Input
                                                        id='orgMemberEmail'
                                                        name='orgMemberEmail'
                                                        caption='E-posta'
                                                        readOnly
                                                        value={pageModel.orgMemberEmail}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Input
                                                        id='orgMemberAddress'
                                                        name='orgMemberAddress'
                                                        caption='Adres'
                                                        required
                                                        multiline
                                                        rows={3}
                                                        value={pageModel.orgMemberAddress}
                                                        onChange={handleChange}
                                                        errorText={inputErrors.find(x => x.name === 'orgMemberAddress')?.errorText}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <div className="py-3">
                                                {legalTextLoading ? (
                                                    <p>Yükleniyor...</p>
                                                ) : (
                                                    legalTexts?.consents
                                                        .filter(
                                                            x =>
                                                                x.type !== LegalContractConsentType.UYELIK_SOZLESMESI &&
                                                                x.type !== LegalContractConsentType.YKB_AYDINLATMA,
                                                        )
                                                        .map((item, index) => (
                                                            <div className="py-1" key={index}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            name={item.type}
                                                                            color="primary"
                                                                            checked={isConsentApproved(item.type)}
                                                                            onChange={handleConsentCheckbox}
                                                                        />
                                                                    }
                                                                    label={
                                                                        <div
                                                                            className={
                                                                                item.required && isKvkkError && !isConsentApproved(item.type)
                                                                                    ? `${classes.errorCheckbox}`
                                                                                    : ''
                                                                            }
                                                                            dangerouslySetInnerHTML={{ __html: item.text }}></div>
                                                                    }
                                                                />
                                                            </div>
                                                        ))
                                                )}
                                            </div>
                                            <Box marginBottom={2} textAlign="center">
                                                {reactStringReplace(
                                                    legalTexts?.consents.find(x => x.type === LegalContractConsentType.YKB_AYDINLATMA)?.text ?? '',
                                                    /<strong>(.*?)<\/strong>/,
                                                    (match, i) => (
                                                        <button style={{ color: '#177897', cursor: 'pointer', border: 'none' }} onClick={handleYkbAggreement}>
                                                            <strong>{match}</strong>
                                                        </button>
                                                    ),
                                                )}
                                            </Box>
                                            <Box marginBottom={2} textAlign="center">
                                                'ONAYLIYORUM' butonuna basılması ile{' '}
                                                <button style={{ color: '#177897', cursor: 'pointer', border: 'none' }} onClick={handleMembershipAggreement}>
                                                    <strong>Üyelik Sözleşmesi</strong>
                                                </button>
                                                'ni okuduğunuzu ve kabul ettiğinizi onaylıyorsunuz.
                                            </Box>
                                        </footer>
                                        <Box display="flex" justifyContent="center" marginBottom={2}>
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                sitekey="6LdqH0QfAAAAAAQR10C9Poo7LItjCQ0Fbe2l9Fnn"
                                                onChange={onChangeCaptcha}
                                            />
                                        </Box>
                                        {errorText && (
                                            <Box display="flex" justifyContent="center" marginBottom={2}>
                                                <Alert severity='error'>{errorText}</Alert>
                                            </Box>
                                        )}
                                        <Box display="flex" justifyContent="center" marginBottom={2}>
                                            <CustomButton
                                                text="ONAYLIYORUM"
                                                className="btn-primary"
                                                disabled={fetchStatus !== FetchStatus.Success || loadingSendForm}
                                                loading={loadingSendForm}
                                                onClick={() => handleSend(pageModel)} />
                                        </Box>
                                    </>
                                ) : (
                                    <div className="success-container">
                                        <TaskAlt className='success-icon' color='success' />
                                        <p>Üyelik için onayınız alınmıştır.</p>
                                    </div>
                                )
                            }
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

const UnderagePermitForm = () => (
    <JoinFormUndeageContextProvider>
        <UnderagePermitFormComponent />
    </JoinFormUndeageContextProvider>
);

export default UnderagePermitForm;